import _             from "underscore";
import * as fuzzball from "fuzzball";

// Levenshtein Distance
// The most common algorithm for measuring edit distance between strings.
// It counts the minimum number of single-character edits needed to transform one string into another.
export function levenshteinDistance( str1: string, str2: string ): number{
    const matrix = Array( str2.length + 1 ).fill( null ).map( () => Array( str1.length + 1 ).fill( null ) );

    for( let i = 0; i <= str1.length; i++ ) matrix[ 0 ][ i ] = i;
    for( let j = 0; j <= str2.length; j++ ) matrix[ j ][ 0 ] = j;

    for( let j = 1; j <= str2.length; j++ ){
        for( let i = 1; i <= str1.length; i++ ){
            const cost = str1[ i - 1 ] === str2[ j - 1 ] ? 0 : 1;
            matrix[ j ][ i ] = Math.min(
                matrix[ j ][ i - 1 ] + 1,     // deletion
                matrix[ j - 1 ][ i ] + 1,     // insertion
                matrix[ j - 1 ][ i - 1 ] + cost // substitution
            );
        }
    }

    return matrix[ str2.length ][ str1.length ];
}

export function levenshteinSimilarity( str1: string, str2: string ): number{
    const distance = levenshteinDistance( str1.toLowerCase(), str2.toLowerCase() );
    const maxLength = Math.max( str1.length, str2.length );
    return 1 - ( distance / maxLength );
}

// Jaro-Winkler Distance
// Particularly good for names as it gives more weight to common prefixes (which often matter in names).
export function jaroSimilarity( str1: string, str2: string ): number{
    if( str1 === str2 ) return 1;

    const len1 = str1.length;
    const len2 = str2.length;
    const matchWindow = Math.floor( Math.max( len1, len2 ) / 2 ) - 1;

    const str1Matches = new Array( len1 ).fill( false );
    const str2Matches = new Array( len2 ).fill( false );

    let matches = 0;
    let transpositions = 0;

    // Find matches
    for( let i = 0; i < len1; i++ ){
        const start = Math.max( 0, i - matchWindow );
        const end = Math.min( i + matchWindow + 1, len2 );

        for( let j = start; j < end; j++ ){
            if( str2Matches[ j ] || str1[ i ] !== str2[ j ] ) continue;
            str1Matches[ i ] = str2Matches[ j ] = true;
            matches++;
            break;
        }
    }

    if( matches === 0 ) return 0;

    // Find transpositions
    let k = 0;
    for( let i = 0; i < len1; i++ ){
        if( !str1Matches[ i ] ) continue;
        while( !str2Matches[ k ] ) k++;
        if( str1[ i ] !== str2[ k ] ) transpositions++;
        k++;
    }

    return ( matches / len1 + matches / len2 + ( matches - transpositions / 2 ) / matches ) / 3;
}

export function jaroWinklerSimilarity( str1: string, str2: string, maxPrefixMatch: number = 4 ): number{
    const jaro = jaroSimilarity( str1.toLowerCase(), str2.toLowerCase() );

    // Find common prefix up to <maxPrefixMatch> characters
    let prefix = 0;
    for( let i = 0; i < Math.min( str1.length, str2.length, maxPrefixMatch ); i++ ){
        if( str1[ i ].toLowerCase() === str2[ i ].toLowerCase() ) prefix++;
        else break;
    }

    return jaro + ( 0.1 * prefix * ( 1 - jaro ) );
}

// Simple N-gram Similarity
// Good for catching typos and rearrangements
export function ngramSimilarity( str1: string, str2: string, sliceSize: number = 2 ): number{
    const getNgrams = ( str: string ): Set<string> => {
        const ngrams = new Set<string>();
        const normalized = str.toLowerCase();
        for( let i = 0; i <= normalized.length - sliceSize; i++ ){
            ngrams.add( normalized.slice( i, i + sliceSize ) );
        }
        return ngrams;
    };

    const ngrams1 = getNgrams( str1 );
    const ngrams2 = getNgrams( str2 );

    const intersection = new Set( [ ...ngrams1 ].filter( x => ngrams2.has( x ) ) );
    const union = new Set( [ ...ngrams1, ...ngrams2 ] );

    return intersection.size / union.size;
}


// General purpose similarity score
// * Jaro-Winkler is often best for names due to prefix weighting
// * Levenshtein is simple and handles typos well
// * N-gram catches character rearrangements
// * Fuzzball catches general similarity
// * Use a threshold of 0.7-0.8 for practical similarity detection
interface SimilarityScore {
    match: string;
    score: number;
    algorithm: string;
}

interface FindSimilarStringsParams {
    minPrefixMatch?: number,
    minTypoSliceCheck?: number,
    fuzzballOptions?: fuzzball.FuzzballBaseOptions,
    useLevenshtein?: boolean,
    useJaroWinkler?: boolean,
    useNgram?: boolean,
    useFuzzballRatio?: boolean,
}

export function findSimilarStrings( subjectString: string, comparisonStrings: string[], threshold: number = 0.8, params: FindSimilarStringsParams = {} ): SimilarityScore[]{

    const results: SimilarityScore[] = [];

    for( const comparisonString of comparisonStrings ){

        // Try multiple algorithms
        const levenshtein   = ( _.isBoolean( params?.useLevenshtein ) && !params.useLevenshtein ) ? 0 : levenshteinSimilarity( subjectString, comparisonString ),
              jaroWinkler   = ( _.isBoolean( params?.useJaroWinkler ) && !params.useJaroWinkler ) ? 0 : jaroWinklerSimilarity( subjectString, comparisonString, params?.minPrefixMatch ?? undefined ),
              ngram         = ( _.isBoolean( params?.useNgram ) && !params.useNgram ) ? 0 : ngramSimilarity( subjectString, comparisonString, params?.minTypoSliceCheck ?? undefined ),
              fuzzballRatio = ( _.isBoolean( params?.useFuzzballRatio ) && !params.useFuzzballRatio ) ? 0 : fuzzball.ratio( subjectString, comparisonString, params?.fuzzballOptions ?? undefined ) / 100;

        // Use the highest score
        const maxScore = Math.max( levenshtein, jaroWinkler, ngram, fuzzballRatio );

        if( maxScore >= threshold ){
            const algorithm = maxScore === levenshtein ? 'levenshtein' :
                              maxScore === jaroWinkler ? 'jaro-winkler' :
                              maxScore === ngram ? 'ngram' : 'fuzzballRatio';

            results.push( {
                match: comparisonString,
                score: maxScore,
                algorithm
            } );
        }
    }

    return results.sort( ( a, b ) => b.score - a.score );
}

export function findTopSimilarityString( subjectString: string, comparisonStrings: string[], threshold: number = 0.8, params: FindSimilarStringsParams = {}, debug = false ): SimilarityScore{

    const similarityResults = findSimilarStrings( subjectString, comparisonStrings, threshold, params ),
          resultScores      = _.pluck( similarityResults, 'score' ),
          maxScore          = Math.max( ...resultScores ),
          maxResultScore    = [ -Infinity, NaN ].includes( maxScore ) ? 0 : maxScore,
          maxResultMatch    = _.findWhere( similarityResults, { score: maxResultScore } ) ?? { match: '', score: -1, algorithm: null };

    if( debug ){
        console.log( similarityResults, resultScores, maxScore, maxResultMatch );
    }

    return ( maxResultMatch.algorithm !== null ) ? maxResultMatch : null;
}
